import { Grid, FormControlLabel, RadioGroup, Radio } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';
import { DocumentDigital } from 'shell/view-models/act-form';
import { memo, useCallback } from 'react';
import { DateTime } from 'luxon';
import { FormControl } from '@mui/material';
import FormPaper from './paper';
import FormDigital from './digital';
import { ActType } from 'shell/constants/act-type';
import { DocumentPaper } from 'shell/view-models/document-paper';
import { ActManagementForm } from 'shell/view-models/act-form';
import { emptyDigital, emptyPaper } from './constants';

interface DocumentActFormProps {
  values?: ActManagementForm;
  initialValues?: ActManagementForm;
  onChange: (
    key: keyof ActManagementForm,
    value: ActManagementForm[keyof ActManagementForm]
  ) => void;
  onChanges: (values: ActManagementForm) => void;
  disabled?: boolean;
  isNotDisabledData?: boolean;
  disabledRadio?: boolean;
  errors?: FormikErrors<ActManagementForm>;
  touched?: FormikTouched<ActManagementForm>;
  isEditMode?: boolean;
  birthday?: DateTime | null;
  visibilityDigital?: { [K in keyof DocumentDigital]: boolean };
  visibilityPaper?: { [K in keyof DocumentPaper]: boolean };
  disabledPaper?: { [K in keyof DocumentPaper]: boolean };
  hideWithoutAct?: boolean;
  requiredDigital?: { [K in keyof DocumentDigital]: boolean };
}

const DocumentActFormComponent = ({
  initialValues = {
    paper: emptyPaper,
    digital: emptyDigital,
  },
  values,
  onChange,
  onChanges,
  errors,
  touched,
  disabled,
  isNotDisabledData = false,
  disabledRadio,
  visibilityPaper,
  disabledPaper,
  hideWithoutAct,
  requiredDigital,
}: DocumentActFormProps): React.ReactElement => {
  const onChangeFormPaper = useCallback(
    (key, value) => {
      onChange('paper', {
        ...values?.paper,
        [key]: value,
      });
    },
    [values?.paper, onChange]
  );

  const onChangeFormDigital = useCallback(
    (key, value) => {
      onChange('digital', {
        ...values?.digital,
        [key]: value,
      });
    },
    [values?.digital, onChange]
  );

  const onChangeFieldsFormDigital = useCallback(
    (valuesDigital) => {
      onChange('digital', {
        ...values?.digital,
        ...valuesDigital,
      });
    },
    [values?.digital, onChange]
  );

  return (
    <Grid item xs={12}>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="Tipo atto"
            id="actType"
            value={values?.actType}
            onChange={(event) => {
              const actType = event.target.value as ActType;
              onChanges({
                ...values,
                actType,
                digital: actType === ActType.AttoDigitale ? initialValues.digital : null,
                paper: actType === ActType.AttoCartaceo ? initialValues.paper : null,
              });
            }}
          >
            <FormControlLabel
              value={ActType.AttoCartaceo}
              control={<Radio />}
              label="Atto cartaceo"
              disabled={disabled || disabledRadio}
            />
            <FormControlLabel
              value={ActType.AttoDigitale}
              control={<Radio />}
              label="Atto digitale"
              disabled={disabled || disabledRadio}
            />
            {!hideWithoutAct ? (
              <FormControlLabel
                value={ActType.SenzaAtto}
                control={<Radio />}
                label="Senza Atto"
                disabled={disabled || disabledRadio}
                hidden={hideWithoutAct}
              />
            ) : undefined}
          </RadioGroup>
        </FormControl>
      </Grid>
      {values?.actType === ActType.AttoDigitale ? (
        <FormDigital
          onChange={onChangeFormDigital}
          onChangeFields={onChangeFieldsFormDigital}
          values={values?.digital || undefined}
          allDisabled={disabled}
          errors={errors?.digital as FormikErrors<DocumentDigital>}
          touched={touched?.digital as unknown as FormikTouched<DocumentDigital>}
          required={requiredDigital}
          isNotDisabledData={isNotDisabledData}
        />
      ) : undefined}
      {values?.actType === ActType.AttoCartaceo ? (
        <FormPaper
          onChange={onChangeFormPaper}
          values={values?.paper || undefined}
          errors={errors?.paper as FormikErrors<DocumentPaper>}
          touched={touched?.paper as unknown as FormikTouched<DocumentPaper>}
          visibility={visibilityPaper}
          disabled={disabledPaper}
          allDisabled={disabled}
          isNotDisabledData={isNotDisabledData}
        />
      ) : undefined}
    </Grid>
  );
};

DocumentActFormComponent.displayName = 'Birthday document component';

const DocumentActForm = memo(DocumentActFormComponent);

export default DocumentActForm;
